//#region Imports
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import {
  faAdd,
  faOctagonExclamation,
} from '@fortawesome/pro-regular-svg-icons';

import { GlobalVariables } from '../../../globalVar';
import customAxios from '../../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../../utils/dateFormat';
import { getToast } from '../../../utils/toastConfig';
import { useTranslationHook } from '../../../hooks/UseTranslationHook';
import usePersistedColumnOrder from '../../../hooks/UseCustomColumnOrder';
import { CreateFailureTypeModal } from '../../Modal/Sigma/FailureTypes/CreateFailureTypeModal';
import { UpdateFailureTypeModal } from '../../Modal/Sigma/FailureTypes/UpdateFailureTypeModal';
import { DeleteFailureTypeModal } from '../../Modal/Sigma/FailureTypes/DeleteFailureTypeModal';
import { useUserPermissions } from '../../../hooks/userPermissions';
//#endregion

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

export const FailureTypesTable = () => {
  const userPermissions = useUserPermissions();

  const [isFetching, setIsFetching] = useState(false); // State for tracking API request status
  const [showCreateModal, setShowCreateModal] = useState(false); // State for showing the create modal
  const [showUpdateModal, setShowUpdateModal] = useState(false); // State for showing the update modal
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing the delete modal
  const [failureTypeId, setFailureTypeId] = useState(null); // State for storing the selected failure type ID
  const [failureTypes, setFailureTypes] = useState([]); // State for storing the fetched regions

  useEffect(() => {
    fetchData(); // Fetch regions data when the component mounts
  }, []);

  const {
    addString,
    editString,
    deleteString,
    statusString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
  } = useTranslationHook();

  const navigate = useNavigate();

  // Define initial table columns
  const initialColumns = [
    { field: 'id', headerName: 'ID', width: LOCAL_COLUMN_WIDTH },
    {
      field: 'name',
      headerName: 'Nombre del servicio',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'is_active',
      headerName: statusString,
      width: 150,
      renderCell: (params) => {
        if (
          params.row.is_active === 'Activo' ||
          params.row.is_active === 'Active'
        ) {
          return (
            <div>
              <span className="statusActive">{params.row.is_active}</span>
            </div>
          );
        } else {
          return (
            <div>
              <span className="statusInactive">{params.row.is_active}</span>
            </div>
          );
        }
      },
      valueGetter: (params) => params.row.is_active,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'created_by_name',
      headerName: createdByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_by_name',
      headerName: updatedByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'notes',
      headerName: notesString,
      width: LOCAL_COLUMN_WIDTH,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  // Define the action column with edit and delete buttons
  const actionColumn = [
    {
      field: 'action',
      headerName: 'Detalle',
      width: 160,
      renderCell: (params) => {
        return (
          <div
            className="cellAction equips-datagrid-actions-tour"
            style={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Failures Button */}
            <Tooltip title="Fallas" arrow>
              <IconButton
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                onClick={() => {
                  navigate(`/serviceTypes/failures/${params.row.id}`);
                }}
              >
                <FontAwesomeIcon
                  icon={faOctagonExclamation}
                  className="detail-icons"
                  id="update-icon"
                />
              </IconButton>
            </Tooltip>

            {/* Edit Button */}
            {userPermissions.includes('Update Failure Types') && (
              <Tooltip title={editString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setFailureTypeId(params.row.id); // Set selected failure type IDID
                    setShowUpdateModal(true); // Show the update modal
                  }}
                >
                  <ModeEditOutlineOutlinedIcon
                    className="detail-icons"
                    id="update-icon"
                  />
                </IconButton>
              </Tooltip>
            )}
            {/* Delete Button */}
            {userPermissions.includes('Delete Failure Types') && (
              <Tooltip title={deleteString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setFailureTypeId(params.row.id); // Set selected failure type ID
                    setShowDeleteModal(true); // Show the delete modal
                  }}
                >
                  <DeleteIcon className="detail-icons" id="delete-icon" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const [columns, updateColumnOrder] = usePersistedColumnOrder(
    'failuresTypesTable',
    initialColumns,
  );

  /**
   * Fetches regions data from the API.
   */
  const fetchData = async () => {
    setIsFetching(true); // Set fetching state to true
    const endpoint = `${GlobalVariables.getURL()}failure-types/`;
    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 204) {
        setFailureTypes([]); // Set regions to an empty array if no data is returned
        return;
      }
      const failureTypesData = response.data.data.map((ft) => {
        return {
          ...ft,
          is_active: t(ft.is_active === 1 ? 'Activo' : 'Inactivo'),
        };
      });

      setFailureTypes(failureTypesData); // Update regions state with fetched data
    } catch (error) {
      console.error('Error fetching regions:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false); // Set fetching state to false
    }
  };

  /**
   * Handles column order changes for the table.
   * @param {Object} params - Parameters containing target and old index.
   */
  const handleColumnOrderChange = (params) => {
    const { targetIndex, oldIndex } = params;

    const updatedColumns = [...columns];
    const [movedColumn] = updatedColumns.splice(oldIndex, 1);
    updatedColumns.splice(targetIndex, 0, movedColumn);

    updateColumnOrder(updatedColumns); // Update column order in persisted state
  };

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">Tipos de servicio</span>
          {userPermissions.includes('Create Failure Types') && (
            <button
              className="add-button create-equip-tour"
              onClick={() => setShowCreateModal(true)}
            >
              {addString}
              <FontAwesomeIcon icon={faAdd} />
            </button>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <CreateFailureTypeModal
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refreshData={fetchData}
      />

      {/* Update Modal */}
      <UpdateFailureTypeModal
        failureTypeId={failureTypeId}
        showModal={showUpdateModal}
        setShowModal={setShowUpdateModal}
        refreshData={fetchData}
      />

      {/* Delete Modal */}
      <DeleteFailureTypeModal
        failureTypeId={failureTypeId}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        refreshData={fetchData}
      />

      {/* Data Grid */}
      <DataGridPro
        rows={failureTypes}
        columns={columns.concat(actionColumn)}
        loading={isFetching}
        onColumnOrderChange={(params) => handleColumnOrderChange(params)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          pinnedColumns: { right: ['action'] },
        }}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No hay tipos de servicios en la base de datos
            </Stack>
          ),
          toolbar: GridToolbar, // Toolbar with default functionality
        }}
      />
    </>
  );
};
